import type { AppAbility } from '@/lib/casl'
import type { Organization } from '@/lib/services/organizations/organizations.types'

const scribeHost = 'scribehow.com'
const unresolvedTokenPattern =
  /(\{[A-Za-z][A-Za-z0-9_]*\}|\$[A-Za-z][A-Za-z0-9_]*|:[A-Za-z][A-Za-z0-9_]*)/

/** Route values a destination URL's `{orgId}`/`{locationId}` tokens resolve from. */
interface DestinationUrlTokens {
  orgId?: number | string | null | undefined
  locationId?: number | string | null | undefined
}

function parseUrl(value: string): URL | null {
  try {
    return new URL(value)
  } catch {
    return null
  }
}

function decodeHtmlEntities(value: string): string {
  return String(value || '')
    .replaceAll('&amp;', '&')
    .replaceAll('&quot;', '"')
    .replaceAll('&#39;', "'")
    .replaceAll('&lt;', '<')
    .replaceAll('&gt;', '>')
}

function getIframeSrc(value: string | null | undefined): string {
  const source = String(value || '').trim()

  if (!/^<iframe[\s>]/i.test(source)) {
    return source
  }

  const quotedSrc = source.match(/<iframe\b[^>]*\bsrc\s*=\s*(["'])(.*?)\1/i)

  if (quotedSrc?.[2]) {
    return quotedSrc[2]
  }

  const unquotedSrc = source.match(/<iframe\b[^>]*\bsrc\s*=\s*([^\s>]+)/i)

  return unquotedSrc?.[1] ?? ''
}

function isScribeHost(hostname: string): boolean {
  const host = String(hostname || '').toLowerCase()

  return host === scribeHost || host.endsWith('.' + scribeHost)
}

function replaceKnownTokens(value: string, { orgId, locationId }: DestinationUrlTokens = {}) {
  let resolved = String(value || '').trim()

  if (orgId) {
    const organizationId = String(orgId)

    resolved = resolved
      .replaceAll('{orgId}', organizationId)
      .replaceAll('{organizationId}', organizationId)
      .replaceAll('$orgId', organizationId)
      .replaceAll('$organizationId', organizationId)
      .replaceAll(':orgId', organizationId)
      .replaceAll(':organizationId', organizationId)
  }

  if (locationId) {
    const resolvedLocationId = String(locationId)

    resolved = resolved
      .replaceAll('{locationId}', resolvedLocationId)
      .replaceAll('$locationId', resolvedLocationId)
      .replaceAll(':locationId', resolvedLocationId)
  }

  return resolved
}

export function isScribeUrl(value: string | null | undefined): boolean {
  const url = parseUrl(String(value || '').trim())

  if (!url) {
    return false
  }

  return ['http:', 'https:'].includes(url.protocol) && isScribeHost(url.hostname)
}

/** The Scribe embed URL in a pasted embed code or share link, or null when there isn't one. */
export function getScribeEmbedSrc(value: string | null | undefined): string | null {
  const src = decodeHtmlEntities(getIframeSrc(value)).trim()
  const url = isScribeUrl(src) ? parseUrl(src) : null

  if (!url) {
    return null
  }

  if (url.pathname.startsWith('/shared/')) {
    url.pathname = url.pathname.replace(/^\/shared\//, '/embed/')
  }

  if (!url.pathname.startsWith('/embed/')) {
    return null
  }

  return url.toString()
}

export function getScribeEmbedUrl(value: string | null | undefined): string | null {
  return getScribeEmbedSrc(value)
}

export function isScribeEmbed(value: string | null | undefined): boolean {
  return Boolean(getScribeEmbedSrc(value))
}

/**
 * The link an app update's call to action opens: absolute URLs as written, admin paths under the
 * current organization. Null when there is no destination or a token can't be resolved yet.
 */
export function resolveAppUpdateDestinationUrl(
  destinationUrl: string | null | undefined,
  { orgId, locationId }: DestinationUrlTokens = {}
): string | null {
  const trimmedUrl = String(destinationUrl || '').trim()

  if (!trimmedUrl) {
    return null
  }

  const resolved = replaceKnownTokens(trimmedUrl, { orgId, locationId })

  if (unresolvedTokenPattern.test(resolved)) {
    return null
  }

  if (/^https?:\/\//i.test(resolved)) {
    return resolved
  }

  const normalizedPath = resolved.startsWith('/') ? resolved : '/' + resolved

  if (!orgId) {
    return normalizedPath
  }

  const orgPrefix = '/' + orgId

  if (normalizedPath === orgPrefix || normalizedPath.startsWith(orgPrefix + '/')) {
    return normalizedPath
  }

  return orgPrefix + normalizedPath
}

type MaybeAbility = AppAbility | null | undefined

export function canCreateAppUpdates(ability: MaybeAbility): boolean {
  return Boolean(ability?.can('access', 'global') && ability?.can('create', 'app-updates'))
}

export function canEditAppUpdates(ability: MaybeAbility): boolean {
  return Boolean(ability?.can('access', 'global') && ability?.can('edit', 'app-updates'))
}

export function canDeleteAppUpdates(ability: MaybeAbility): boolean {
  return Boolean(ability?.can('access', 'global') && ability?.can('delete', 'app-updates'))
}

export function canViewUnpublishedAppUpdates(ability: MaybeAbility): boolean {
  return Boolean(
    ability?.can('access', 'global') &&
    (ability?.can('create', 'app-updates') ||
      ability?.can('edit', 'app-updates') ||
      ability?.can('delete', 'app-updates'))
  )
}

export function canManageAppUpdates(ability: MaybeAbility): boolean {
  return Boolean(
    ability?.can('access', 'global') &&
    (ability?.can('edit', 'app-updates') || ability?.can('delete', 'app-updates'))
  )
}

/** `OrganizationResource` sends `type` as the `OrganizationType` value. */
export function isGlobalOrganizationContext(
  organization: Pick<Organization, 'type'> | null | undefined
): boolean {
  return organization?.type === 'global'
}

export function shouldAutoMarkAppUpdateRead({
  isImpersonating,
}: { isImpersonating?: boolean | undefined } = {}): boolean {
  return !isImpersonating
}
